import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { AlertCircle, ShieldOff } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { AddressDisplay } from '@/components/data/AddressDisplay'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState } from '@/components/data/EmptyState'
import { PlayerStatusBadge } from '@/components/players/PlayerStatusBadge'
import { KycTierBadge } from '@/components/players/KycTierBadge'
import { PlayerActionsMenu } from '@/components/players/PlayerActionsMenu'
import { PlayerClosureNotice } from '@/components/players/PlayerClosureNotice'
import { MlroActionsMenu } from '@/components/players/actions/MlroActionsMenu'
import { RoleGate } from '@/components/auth/RoleGate'
import { Badge } from '@/components/ui/badge'
import { PlayerDetailView } from '@/components/players/detail/PlayerDetailView'
import { PendingGateRevocationsProvider } from '@/components/players/PendingGateRevocations'
import { PlayerChainSelector } from '@/components/players/PlayerChainSelector'
import { BetaTesterToggle } from '@/components/players/BetaTesterToggle'
import { usePlayerRegistryRecord } from '@/hooks/chain/usePlayerRegistryRecord'
import { useKycRegistryRecord } from '@/hooks/chain/useKycRegistryRecord'
import { usePlayerActiveChains } from '@/hooks/chain/usePlayerActiveChains'
import { usePlayerDetail } from '@/hooks/api/usePlayerDetail'
import { ACTIVE_CHAINS, chainName } from '@/config/chains'
import { getContractAddress } from '@/config/contracts'
import { ApiError } from '@/lib/api'
import { formatTimestamp, isHexAddress } from '@/lib/utils'
import type { SelfExclusionRecord } from '@/types/player-detail'

export const Route = createFileRoute('/players/$address')({
    component: PlayerDetailPage,
})

function PlayerDetailPage() {
    const { address } = Route.useParams()
    return <PlayerDetailPageInner address={address} />
}

export function PlayerDetailPageInner({ address }: { address: string }) {
    const valid = isHexAddress(address)
    const activity = usePlayerActiveChains(valid ? address : undefined)
    const [chainOverride, setChainOverride] = useState<number | null>(null)

    // Default the selector to the first chain where the player is registered;
    // fall back to the first active chain. Operator can override.
    const defaultChainId = activity.isLoading
        ? null
        : (activity.activeChainIds[0] ?? ACTIVE_CHAINS[0]?.id ?? null)
    const selectedChainId = chainOverride ?? defaultChainId
    // The service's lifetime figures and ledger follow the selected chain, so they match the
    // on-chain reads and tx links on the same page (BLO-991).
    const detailQuery = usePlayerDetail(valid ? address : undefined, selectedChainId)

    if (!valid) {
        return (
            <div className="space-y-2">
                <h1 className="text-2xl font-semibold">Invalid address</h1>
                <p className="text-muted-foreground font-mono text-sm">{address}</p>
            </div>
        )
    }

    const body = (
        <div className="space-y-6">
            <header className="flex flex-wrap items-start justify-between gap-4">
                <div className="space-y-1">
                    <h1 className="text-2xl font-semibold">Player</h1>
                    <AddressDisplay address={address} truncate={false} />
                </div>
                <div className="flex flex-wrap items-start gap-4">
                    {/* Person-scoped, not per chain (BLO-999): it sits above the chain
                        selector and keeps its value when the chain changes. */}
                    <BetaTesterToggle address={address} />
                    {/* Admin or MLRO: override-tier is admin-reachable
                        server-side; MLRO-only items are gated inside the menu. */}
                    {detailQuery.data ? (
                        <RoleGate roles={['admin', 'mlro']}>
                            <MlroActionsMenu
                                address={address}
                                detail={detailQuery.data}
                                chainId={selectedChainId}
                            />
                        </RoleGate>
                    ) : null}
                </div>
            </header>

            <PlayerClosureNotice address={address} />

            <PlayerChainSelector
                selected={selectedChainId}
                onSelect={setChainOverride}
                activity={activity}
            />

            <div className="grid gap-4 md:grid-cols-2">
                {ACTIVE_CHAINS.map((c) => (
                    <ChainCard key={c.id} chainId={c.id} address={address} />
                ))}
            </div>

            <DetailSection address={address} query={detailQuery} chainId={selectedChainId} />
        </div>
    )

    if (!detailQuery.data) return body

    return (
        <PendingGateRevocationsProvider address={address} gates={detailQuery.data.gates}>
            {body}
        </PendingGateRevocationsProvider>
    )
}

function SelfExclusionBanner({ record }: { record: SelfExclusionRecord }) {
    const endsLabel = record.endsAt ? formatTimestamp(record.endsAt) : 'permanent'
    return (
        <div className="border-destructive/40 bg-destructive/5 flex flex-wrap items-start gap-3 rounded-md border p-3 text-sm">
            <ShieldOff className="text-destructive mt-0.5 h-4 w-4" />
            <div className="flex-1 space-y-0.5">
                <div className="text-destructive font-medium">Self-exclusion active</div>
                <div className="text-muted-foreground text-xs">
                    Applied {formatTimestamp(record.startsAt)} by {record.appliedBy}
                    {record.appliedByUserId ? ` (${record.appliedByUserId})` : ''} ·
                    {record.endsAt ? ` ends ${endsLabel}` : ' permanent (MLRO lift only)'}
                </div>
                {record.reason ? (
                    <div className="text-muted-foreground text-xs">Reason: {record.reason}</div>
                ) : null}
            </div>
            <Badge variant="destructive">{record.duration}</Badge>
        </div>
    )
}

function DetailSection({
    address,
    query,
    chainId,
}: {
    address: string
    query: ReturnType<typeof usePlayerDetail>
    chainId: number | null
}) {
    if (query.isLoading) {
        return (
            <div className="space-y-3">
                <Skeleton className="h-9 w-full max-w-xl" />
                <Skeleton className="h-64 w-full" />
            </div>
        )
    }

    if (query.isError) {
        const err = query.error
        const notFound = err instanceof ApiError && err.status === 404
        const unauthorized = err instanceof ApiError && err.status === 401
        const forbidden = err instanceof ApiError && err.status === 403
        return (
            <EmptyState
                icon={AlertCircle}
                title={
                    notFound
                        ? 'Player not found'
                        : unauthorized
                          ? 'Session expired'
                          : forbidden
                            ? 'No permission to view this player'
                            : 'Could not load player detail'
                }
                description={
                    notFound
                        ? 'No KYC/compliance record has been created for this address yet.'
                        : err instanceof Error
                          ? err.message
                          : 'Unknown error'
                }
            />
        )
    }

    if (!query.data) return null

    if (chainId === null) {
        return (
            <div className="space-y-3">
                <Skeleton className="h-9 w-full max-w-xl" />
                <Skeleton className="h-64 w-full" />
            </div>
        )
    }

    return (
        <div className="space-y-4">
            {query.data.selfExclusion ? (
                <SelfExclusionBanner record={query.data.selfExclusion} />
            ) : null}
            <PlayerDetailView address={address} detail={query.data} chainId={chainId} />
        </div>
    )
}

export function ChainCard({ chainId, address }: { chainId: number; address: string }) {
    const status = usePlayerRegistryRecord(chainId, address)
    const tier = useKycRegistryRecord(chainId, address)
    const playerRegistry = getContractAddress(chainId, 'playerRegistry')
    const kycRegistry = getContractAddress(chainId, 'kycRegistry')

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center justify-between gap-2">
                    <div>
                        <CardTitle>{chainName(chainId)}</CardTitle>
                        <CardDescription>chain id {chainId}</CardDescription>
                    </div>
                    {/* Suspend/reinstate/ban are service-mediated: the
                        service's signer holds the on-chain OPERATOR_ROLE, so the
                        menu gates on the console role only. */}
                    <RoleGate
                        roles={['admin', 'mlro']}
                        fallback={<Badge variant="muted">read-only</Badge>}
                    >
                        <PlayerActionsMenu address={address} chainId={chainId} />
                    </RoleGate>
                </div>
            </CardHeader>
            <CardContent className="space-y-3">
                {!playerRegistry ? (
                    <p className="text-muted-foreground text-xs">
                        PlayerRegistry address not configured for this chain.
                    </p>
                ) : status.isLoading && !status.data ? (
                    <Skeleton className="h-6 w-32" />
                ) : status.isError ? (
                    <p className="text-destructive text-xs">Could not read player status.</p>
                ) : (
                    <Row label="Player status">
                        <PlayerStatusBadge status={status.data?.status} />
                    </Row>
                )}

                {!kycRegistry ? (
                    <p className="text-muted-foreground text-xs">
                        KYCRegistry address not configured for this chain.
                    </p>
                ) : tier.isLoading && !tier.data ? (
                    <Skeleton className="h-6 w-32" />
                ) : tier.isError ? (
                    <p className="text-destructive text-xs">Could not read KYC tier.</p>
                ) : (
                    <Row label="KYC tier">
                        <KycTierBadge tier={tier.data?.tier} />
                    </Row>
                )}
            </CardContent>
        </Card>
    )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{label}</span>
            {children}
        </div>
    )
}
